import Http from '../../network/Http'
import {
    AVAILABLE_OR_NOT,
    HAVE_OR_NOT,
    PROXY_TYPE,
    RESOLUTION_TYPE,
    VOTE_STATUS,
    VOTING_PREFERENCE_RESPONSE,
    VOTING_PREFERENCE_STATUS,
    VOTING_PREFERENCE_STATUS_SUMMARY
} from '../../CodeMaster'
import {ContentType} from '../../network/Constants'
import {NatBaseResponseInterface} from '../../__NoAutomatedTests/components/common/repository/NatBaseResponseInterface'
import {Nullable} from '../../app/TypeHelpers'
import {AssocIdName} from './model/AssocIdName'
import {GeneralMeetingFile} from './model/GeneralMeetingFile'
import {GeneralMeetingProposalsOfAssoc} from './model/GeneralMeetingProposalsOfAssoc'
import {GeneralMeetingQuestionsOfAssoc} from './model/GeneralMeetingQuestionsOfAssoc'

export interface GetUnitOwnerByLoginUserRoomResponse extends NatBaseResponseInterface {
    details: Nullable<UnitOwner>
}

export interface UnitOwner {
    userId: string
    hasekoId: string
    name: string
}

export interface CheckUnitOwnerConfirmationKeyResponse extends NatBaseResponseInterface {
    details: null
}

export interface GetUnitOwnershipByLoginUserResponse extends NatBaseResponseInterface {
    details: Nullable<HAVE_OR_NOT>
}

export interface GetResidentGeneralMeetingInfoListResponse extends NatBaseResponseInterface {
    details: ResidentGeneralMeetingInfo[]
}

export interface ResidentGeneralMeetingInfo {
    generalMeetingId: number
    generalMeetingName: Nullable<string>
    generalMeetingDate: Date
    votingPreferenceDeadline: Date
    votingPreferenceStatusSummary: VOTING_PREFERENCE_STATUS_SUMMARY
}

export interface ResidentGeneralMeetingResponse extends NatBaseResponseInterface {
    details: ResidentGeneralMeeting
}

export interface ResidentGeneralMeeting {
    generalMeetingId: number
    generalMeetingName: Nullable<string>
    assocIdNameList: AssocIdName[]
    generalMeetingDate: Date,
    noticeContent: Nullable<string>
    generalMeetingFileList: GeneralMeetingFile[]
    votingPreferenceDeadline: Date
    residentVotingPreferenceList: ResidentVotingPreference[]
    questionAvailability: AVAILABLE_OR_NOT
    questionDeadline: Nullable<Date>
    generalMeetingQuestionsOfAssocList: GeneralMeetingQuestionsOfAssoc[]
    generalMeetingProposalsOfAssocList: GeneralMeetingProposalsOfAssoc[]
}

export interface ResidentVotingPreference {
    votingPreferenceId: Nullable<number>
    assocId: number
    assocName: string
    votingPreferenceStatus: VOTING_PREFERENCE_STATUS
    votingPreferenceResponse: Nullable<VOTING_PREFERENCE_RESPONSE>
    residentProxyVoter: Nullable<ResidentProxyVoter>
    residentProposalResponseList: Nullable<ResidentProposalResponse[]>
    version: Nullable<number>
}

export interface ResidentProxyVoter {
    proxyType: PROXY_TYPE
    proxyVoterRidgeCd: Nullable<string>
    proxyVoterRidgeName: Nullable<string>
    proxyVoterRoomNumber: Nullable<string>
    proxyVoterName: Nullable<string>
}

export interface ResidentProposalResponse {
    proposalId: number
    proposalResponse: VOTE_STATUS
}

interface SaveGeneralMeetingVotingPreferenceResponse extends NatBaseResponseInterface {
    details: null
}

interface RegisterGeneralMeetingQuestionResponse extends NatBaseResponseInterface {
    details: number
}

interface DeleteGeneralMeetingQuestionResponse extends NatBaseResponseInterface {
    details: number
}

export default class ResidentGeneralMeetingRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async getUnitOwnerByLoginUserRoom(): Promise<GetUnitOwnerByLoginUserRoomResponse> {
        return await this.http.get(
            '/api/resident/login-user/room/unit-owner',
        )
    }

    async updateUnitOwnershipByLoginUserRoom(
        unitOwnerConfirmationKey: string,
        unitOwnership: HAVE_OR_NOT,
    ): Promise<void> {
        return await this.http.put(
            '/api/resident/login-user/unit-ownership',
            {
                unitOwnerConfirmationKey: unitOwnerConfirmationKey,
                unitOwnership: unitOwnership.value,
            },
        )
    }

    async checkUnitOwnerConfirmationKey(
        unitOwnerConfirmationKey: string,
    ): Promise<CheckUnitOwnerConfirmationKeyResponse> {
        return await this.http.post(
            '/api/resident/login-user/condominium/unit-owner-confirmation-key-validations',
            {
                unitOwnerConfirmationKey: unitOwnerConfirmationKey,
            },
            ContentType.JSON
        )
    }

    async getUnitOwnershipByLoginUser(): Promise<GetUnitOwnershipByLoginUserResponse> {
        const apiResponseJSON = await this.http.get(
            '/api/resident/login-user/unit-ownership',
        )

        const apiResponse: GetUnitOwnershipByLoginUserResponse = {
            ...apiResponseJSON,
            details: apiResponseJSON.details ? HAVE_OR_NOT.codeToEnumConstants(apiResponseJSON.details) : null,
        }

        return apiResponse
    }

    async getResidentGeneralMeetingInfoList(
        condominiumCd: string,
        pastFlag: boolean,
        needsVotingPreferenceFlag: boolean,
    ): Promise<GetResidentGeneralMeetingInfoListResponse> {
        const queryParameter: string[] = [`pastFlag=${pastFlag}`]
        if (needsVotingPreferenceFlag) {
            queryParameter.push(`needsVotingPreferenceFlag=true`)
        }

        const apiResponseJSON = await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/general-meetings?` + queryParameter.join('&'),
        )

        const apiResponse = {
            ...apiResponseJSON,
            details: apiResponseJSON.details.map(
                (details: any) => ({
                    ...details,
                    generalMeetingDate: new Date(details.generalMeetingDate),
                    votingPreferenceDeadline: new Date(details.votingPreferenceDeadline),
                    votingPreferenceStatusSummary:
                        VOTING_PREFERENCE_STATUS_SUMMARY.codeToEnumConstants(details.votingPreferenceStatusSummary),
                })
            )
        }

        return apiResponse
    }

    async getResidentGeneralMeeting(
        condominiumCd: string,
        generalMeetingId: number
    ): Promise<ResidentGeneralMeetingResponse> {
        const apiResponseJSON = await this.http.get(
            `/api/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
        )

        const residentVotingPreferenceList: ResidentVotingPreference[] =
            apiResponseJSON.details.residentVotingPreferenceList.map(
                (responseVotingPreference: any) => ({
                    ...responseVotingPreference,
                    votingPreferenceStatus:
                        VOTING_PREFERENCE_STATUS.codeToEnumConstants(responseVotingPreference.votingPreferenceStatus),
                    votingPreferenceResponse:
                        responseVotingPreference.votingPreferenceResponse
                            ? VOTING_PREFERENCE_RESPONSE.codeToEnumConstants(
                                responseVotingPreference.votingPreferenceResponse
                            )
                            : responseVotingPreference.votingPreferenceResponse,
                    residentProxyVoter:
                        responseVotingPreference.residentProxyVoter
                            ? {
                                ...responseVotingPreference.residentProxyVoter,
                                proxyType:
                                    PROXY_TYPE.codeToEnumConstants(
                                        responseVotingPreference.residentProxyVoter.proxyType
                                    ),
                            }
                            : responseVotingPreference.residentProxyVoter,
                    residentProposalResponseList:
                        responseVotingPreference.residentProposalResponseList
                            ? responseVotingPreference.residentProposalResponseList.map(
                                (residentProposalResponse: any) => ({
                                    proposalId: residentProposalResponse.proposalId,
                                    proposalResponse:
                                        VOTE_STATUS.codeToEnumConstants(residentProposalResponse.proposalResponse),
                                })
                            )
                            : responseVotingPreference.residentProposalResponseList
                })
            )

        const apiResponse = {
            ...apiResponseJSON,
            details: {
                ...apiResponseJSON.details,
                generalMeetingDate: new Date(apiResponseJSON.details.generalMeetingDate),
                votingPreferenceDeadline: new Date(apiResponseJSON.details.votingPreferenceDeadline),
                residentVotingPreferenceList: residentVotingPreferenceList,
                questionAvailability:
                    AVAILABLE_OR_NOT.codeToEnumConstants(apiResponseJSON.details.questionAvailability),
                questionDeadline: apiResponseJSON.details.questionDeadline
                    ? new Date(apiResponseJSON.details.questionDeadline)
                    : null,
                generalMeetingQuestionsOfAssocList: apiResponseJSON.details.generalMeetingQuestionsOfAssocList
                    ? apiResponseJSON.details.generalMeetingQuestionsOfAssocList.map(
                        (questionOfAssoc: any) => ({
                            ...questionOfAssoc,
                            generalMeetingQuestionList: questionOfAssoc.generalMeetingQuestionList.map(
                                (question: any) => ({
                                    ...question,
                                    proposal: {
                                        ...question.proposal,
                                        resolutionType:
                                            RESOLUTION_TYPE.codeToEnumConstants(question.proposal.resolutionType),
                                    },
                                    questionSendDateTime: new Date(question.questionSendDateTime),
                                })
                            ),
                        })
                    )
                    : apiResponseJSON.details.generalMeetingQuestionsOfAssocList,
                generalMeetingProposalsOfAssocList: apiResponseJSON.details.generalMeetingProposalsOfAssocList
                    ? apiResponseJSON.details.generalMeetingProposalsOfAssocList.map(
                        (proposalsOfAssoc: any) => ({
                            ...proposalsOfAssoc,
                            generalMeetingProposalList: proposalsOfAssoc.generalMeetingProposalList.map(
                                (proposal: any) => ({
                                    ...proposal,
                                    resolutionType: RESOLUTION_TYPE.codeToEnumConstants(proposal.resolutionType),
                                })
                            ),
                        })
                    )
                    : apiResponseJSON.details.generalMeetingProposalsOfAssocList,
            },
        }

        return apiResponse
    }

    async saveGeneralMeetingVotingPreference(
        condominiumCd: string,
        generalMeetingId: number,
        assocId: number,
        votingPreferenceId: Nullable<number>,
        votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE,
        residentProxyVoter: Nullable<ResidentProxyVoter>,
        residentProposalResponseList: Nullable<ResidentProposalResponse[]>,
        votingPreferenceVersion: Nullable<number>,
        confirmed: boolean,
    ): Promise<SaveGeneralMeetingVotingPreferenceResponse> {
        return await this.http.put(
            `/api/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference`,
            {
                votingPreferenceId: votingPreferenceId,
                votingPreferenceResponse: votingPreferenceResponse.value,
                residentProxyVoter: residentProxyVoter
                    ? {
                        ...residentProxyVoter,
                        proxyType: residentProxyVoter.proxyType.value,
                    }
                    : null,
                residentProposalResponseList: residentProposalResponseList
                    ? residentProposalResponseList.map(residentProposalResponse => ({
                        proposalId: residentProposalResponse.proposalId,
                        proposalResponse: residentProposalResponse.proposalResponse.value,
                    }))
                    : null,
                votingPreferenceVersion: votingPreferenceVersion,
                confirmed: confirmed,
            },
        )
    }

    async registerGeneralMeetingQuestion(
        condominiumCd: string,
        generalMeetingId: number,
        assocId: number,
        proposalId: number,
        question: string,
    ): Promise<RegisterGeneralMeetingQuestionResponse> {
        return await this.http.post(
            `/api/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/proposals/${proposalId}/questions`,
            {
                question: question,
            },
            ContentType.JSON,
        )
    }

    async deleteGeneralMeetingQuestion(
        condominiumCd: string,
        generalMeetingId: number,
        assocId: number,
        proposalId: number,
        questionId: number,
    ): Promise<DeleteGeneralMeetingQuestionResponse> {
        return await this.http.delete(
            `/api/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/proposals/${proposalId}/questions/${questionId}`,
        )
    }
}
